import {
    SpaceBetween,
    Container,
    FormField,
    Header,
    Button,
    Alert,
} from '@amzn/awsui-components-react'
import { Input } from 'antd'
import { ProjectType } from '../../Constant'
import BulkCreateProjectTable from './BulkCreateProjectTable'
import useStore from '../../Store'
import { isFeatureEnabled } from '../../common/FeatureFlags'
import {
    createNewProject,
    createNewProjectUnderProgram,
    createNewServiceProject,
    createNewServiceProjectUnderProgram,
    getNewProjectChange,
    getNewProjectDelete,
    NewProject,
    NewServiceProject,
} from './projectUtil'
import _ from 'lodash'

interface CreateProjectModalLayoutProps {
    preSelectedProgram: any
    programMetadata: any
    selectedYear: any
    newProjects: (NewProject | NewServiceProject)[]
    setNewProjects: React.Dispatch<React.SetStateAction<NewProject[]>>
    canCreateProject: boolean
    canAdmin: boolean
}

const CreateProjectModalContent = (props: CreateProjectModalLayoutProps) => {
    /**
     * Note: Create project occurs in three locations in Falcon:
     * Allocations Page, Projects Page, and Program specific projects page.
     * All of these locations relies on this component for creation of projects.
     */
    const {
        preSelectedProgram,
        programMetadata,
        selectedYear,
        newProjects,
        setNewProjects,
        canCreateProject,
        canAdmin,
    } = props
    const selectBusinessEntity = useStore((state) => state.selectBusinessEntity)
    // Service project creation is being deprecated (HeronServiceManagement).
    // When hidden, the "From Service" entry point is removed and the remaining
    // create control is a plain "Add Project" button. Existing service projects
    // stay viewable.
    const hideServiceProjectCreation = isFeatureEnabled('HIDE_SERVICE_PROJECT_CREATION')
    const servicesOnboarded = selectBusinessEntity.services_onboarded && !hideServiceProjectCreation

    const createNewProjectBulkTable = (): NewProject => {
        return preSelectedProgram
            ? createNewProjectUnderProgram(preSelectedProgram)
            : createNewProject()
    }

    const createNewServiceProjectBulkTable = (): NewServiceProject => {
        return preSelectedProgram
            ? createNewServiceProjectUnderProgram(preSelectedProgram)
            : createNewServiceProject()
    }

    const handleAddProject = (projectType: ProjectType) => {
        const projectToAdd =
            projectType === ProjectType.PROJECT
                ? createNewProjectBulkTable()
                : createNewServiceProjectBulkTable()
        setNewProjects([...newProjects, projectToAdd])
    }

    const handleNewProjectDelete = (
        id: string,
        newProjects: NewProject[] | NewServiceProject[],
    ) => {
        const tempNewProjects = getNewProjectDelete(newProjects, id)
        setNewProjects(tempNewProjects)
    }

    const handleNewProjectChange = (id: string, type: string, value: any) => {
        setNewProjects(getNewProjectChange(newProjects, id, type, value))
    }

    return (
        <SpaceBetween direction='vertical' size='s'>
            <Alert>Projects can only be created for the latest Actuals year.</Alert>
            <Container>
                <SpaceBetween direction='vertical' size='xs'>
                    <Header
                        actions={
                            <SpaceBetween direction='horizontal' size='xs'>
                                <Button
                                    onClick={() => {
                                        handleAddProject(ProjectType.PROJECT)
                                    }}
                                    disabled={!canCreateProject && !canAdmin}
                                    disabledReason='Insufficient permissions to create a project from scratch. Please submit a project request instead.'
                                >
                                    {servicesOnboarded ? 'From Scratch' : 'Add Project'}
                                </Button>
                                {servicesOnboarded && (
                                    <Button
                                        onClick={() => {
                                            handleAddProject(ProjectType.SERVICE_PROJECT)
                                        }}
                                        disabled={
                                            !_.isEmpty(Object.keys(programMetadata)) &&
                                            !programMetadata?.is_true_program
                                        }
                                        disabledReason='Service projects can only be created for true programs.'
                                    >
                                        From Service
                                    </Button>
                                )}
                            </SpaceBetween>
                        }
                    >
                        <SpaceBetween size='m' direction='horizontal'>
                            <FormField
                                label='Year'
                                description='Actuals Year that the project belongs to'
                            >
                                <Input readOnly={true} value={selectedYear}></Input>
                            </FormField>
                            <FormField
                                label='Business Entity'
                                description='Business entity that the project belongs to'
                            >
                                <Input readOnly={true} value={selectBusinessEntity.name}></Input>
                            </FormField>
                        </SpaceBetween>
                    </Header>
                    <BulkCreateProjectTable
                        newProjects={newProjects}
                        onNewProjectDelete={handleNewProjectDelete}
                        onNewProjectChange={handleNewProjectChange}
                        isProgramPreSelected={preSelectedProgram !== null}
                        selectedYear={selectedYear}
                    />
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    )
}

export default CreateProjectModalContent
